import MuiModal from "@mui/material/Modal";
import { useAnimateHeight } from "@redotech/react-animation/transition";
import * as classNames from "classnames";
import {
  HTMLAttributes,
  ReactNode,
  createContext,
  memo,
  useContext,
  useEffect,
  useState,
} from "react";
import { IconButton } from "./button";
import CloseIcon from "./icon/modal-close-button.svg";
import * as modalCss from "./modal.module.css";

/**
 * Container element for modals
 */
export const ModalRootContext = createContext<HTMLElement>(document.body);

export const ModalContainer = memo(
  ({
    children,
    ...attributes
  }: { children: ReactNode | ReactNode[] } & HTMLAttributes<HTMLElement>) => {
    const modalRoot = useContext(ModalRootContext);
    const [ref, setRef] = useState(modalRoot);
    return (
      <ModalRootContext.Provider value={ref}>
        {children}
        <div ref={setRef as any} {...attributes}></div>
      </ModalRootContext.Provider>
    );
  },
);

export type ModalSize =
  | typeof ModalSize.SKINNY
  | typeof ModalSize.SMALL
  | typeof ModalSize.MEDIUM
  | typeof ModalSize.LARGE;

export namespace ModalSize {
  export const SKINNY = Symbol("Skinny");
  export const SMALL = Symbol("Small");
  export const MEDIUM = Symbol("Medium");
  export const LARGE = Symbol("Large");
}

const modalSizeClasses = {
  [ModalSize.SKINNY]: modalCss.skinny,
  [ModalSize.SMALL]: modalCss.small,
  [ModalSize.MEDIUM]: modalCss.medium,
  [ModalSize.LARGE]: undefined,
};

export type PaddingAmount =
  | typeof PaddingAmount.NONE
  | typeof PaddingAmount.SMALL
  | typeof PaddingAmount.MEDIUM
  | typeof PaddingAmount.LARGE;

export namespace PaddingAmount {
  export const NONE = Symbol("None");
  export const SMALL = Symbol("Small");
  export const MEDIUM = Symbol("Medium");
  export const LARGE = Symbol("Large");
}

const paddingAmountClasses = {
  [PaddingAmount.NONE]: modalCss.noPadding,
  [PaddingAmount.SMALL]: modalCss.smallPadding,
  [PaddingAmount.MEDIUM]: modalCss.mediumPadding,
  [PaddingAmount.LARGE]: undefined,
};

export const Modal = memo(
  ({
    children,
    open,
    footer,
    onClose,
    size = ModalSize.MEDIUM,
    paddingAmount = PaddingAmount.MEDIUM,
    title,
    subtitle,
    hideCloseButton = false,
    showHeaderBorder = true,
    showFooterBorder = false,
    className,
  }: {
    children: ReactNode;
    open: boolean;
    onClose?(): void;
    size?: ModalSize;
    paddingAmount?: PaddingAmount;
    footer?: ReactNode | ReactNode[];
    title: ReactNode;
    subtitle?: ReactNode;
    hideCloseButton?: boolean;
    showHeaderBorder?: boolean;
    showFooterBorder?: boolean;
    className?: string;
  }) => {
    const [element, setElement] = useState<HTMLElement | null>(null);
    const [properties] = useAnimateHeight(element);

    useEffect(() => {
      // Blocks background scrolling while modal is open
      document.documentElement.style.overflow = open ? "hidden" : "";
      return () => {
        document.documentElement.style.overflow = "";
      };
    }, [open]);

    const headerClassname = [modalCss.header];
    if (showHeaderBorder) {
      headerClassname.push(modalCss.headerBorder);
    }
    if (paddingAmount === PaddingAmount.NONE) {
      headerClassname.push(modalCss.headerPadding);
    }

    const footerClassname = [modalCss.footer];
    if (showFooterBorder) {
      footerClassname.push(modalCss.footerBorder);
    }

    return (
      <MuiModal
        open={open}
        onClose={onClose}
        className={modalCss.modalContainer}
      >
        <article
          ref={setElement}
          className={classNames(
            modalCss.modal,
            modalSizeClasses[size],
            paddingAmountClasses[paddingAmount],
            className,
          )}
          style={properties}
        >
          <header className={classNames(headerClassname)}>
            <div>
              <h1 className={modalCss.title}>{title}</h1>
              {subtitle && <div className={modalCss.subtitle}>{subtitle}</div>}
            </div>

            {!hideCloseButton && (
              <div>
                <IconButton onClick={onClose}>
                  <CloseIcon />
                </IconButton>
              </div>
            )}
          </header>
          {children}
          {footer && (
            <footer className={classNames(footerClassname)}>{footer}</footer>
          )}
        </article>
      </MuiModal>
    );
  },
);
